import { useState } from "react";
import { useAction } from "convex/react";
import { Loader2, X } from "lucide-react";
import type { FunctionReturnType } from "convex/server";
import { api } from "../../../convex/_generated/api";
import { cn } from "../../lib/cn";
import { useLoggingFlow } from "../../lib/logging-flow";
import type { LoggingFlowReviewItem } from "../../lib/logging-flow";
import { CreditDrawer, isCreditError } from "../CreditDrawer";
import type { MealReviewPayload, MealType } from "../meal-review/review-utils";

// Text describe flow (plan todo 14). The user types a meal description, the
// api.analyze.analyzeMealText action parses it into structured items, a
// structured-card preview (item chips, NOT a chat paragraph) confirms what was
// detected, and "Review meal" routes into the unified Meal Review screen via
// startFlow("review", {...source:"text"}). This flow NEVER routes through the
// agent/chat.

export type MealTextAnalysis = FunctionReturnType<typeof api.analyze.analyzeMealText>;

export const TEXT_PLACEHOLDER =
  "Two rotis, one bowl of chicken karahi and half a glass of Coke";

export const TEXT_MEAL_TYPES: readonly MealType[] = ["breakfast", "lunch", "dinner", "snack"];

const MEAL_TYPE_LABELS: Record<MealType, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snack",
};

// ---------------------------------------------------------------------------
// Pure logic (the interactivity seam for the repo's node-env test pattern:
// renderToStaticMarkup + pure functions, NO jsdom).
// ---------------------------------------------------------------------------

export function canAnalyzeText(description: string): boolean {
  return description.trim().length > 0;
}

export type AnalysisErrorKind = "credits" | "failed" | "unknown";

// analyzeMealText throws ConvexError INSUFFICIENT_CREDITS / ANALYSIS_FAILED /
// VALIDATION. ConvexError embeds the data payload in the client message, so a
// message scan catches both the code and the human message ("Meal analysis
// failed"). VALIDATION cannot happen here (Analyze is disabled while empty)
// and falls into "unknown".
export function classifyAnalysisError(error: unknown): AnalysisErrorKind {
  if (isCreditError(error)) return "credits";
  const message = error instanceof Error ? error.message : String(error);
  if (message.includes("ANALYSIS_FAILED") || /analysis failed/i.test(message)) return "failed";
  return "unknown";
}

// Validator nulls become undefined; unknown nutrition stays UNKNOWN end-to-end
// (the review screen and logMealBatch never zero-fill).
export function mapAnalysisItems(analysis: MealTextAnalysis): LoggingFlowReviewItem[] {
  return analysis.items.map((item) => ({
    name: item.name,
    ...(item.quantity === null ? {} : { quantity: item.quantity }),
    ...(item.unit === null ? {} : { unit: item.unit }),
    ...(item.calories === null ? {} : { calories: item.calories }),
    ...(item.proteinG === null ? {} : { proteinG: item.proteinG }),
    ...(item.carbsG === null ? {} : { carbsG: item.carbsG }),
    ...(item.fatG === null ? {} : { fatG: item.fatG }),
    ...(item.fiberG === null ? {} : { fiberG: item.fiberG }),
    dataSource: item.dataSource,
    confidence: item.confidence,
  }));
}

// Review payload contract (todo 12 MealReviewPayload): items + source "text"
// plus optional name/mealType/uncertainty extras. An explicit meal-type
// preselect wins over the AI guess; uncertainty rides along only when the AI
// returned a real bounded range.
export function buildTextReviewPayload(
  analysis: MealTextAnalysis,
  options: { readonly mealType?: MealType },
): MealReviewPayload {
  const { hasRange, lowKcal, highKcal, reason } = analysis.uncertainty;
  const uncertainty =
    hasRange && lowKcal !== null && highKcal !== null
      ? { lowKcal, highKcal, reason: reason ?? "The AI gave a calorie range for this meal." }
      : undefined;
  const mealType = options.mealType ?? analysis.mealTypeGuess ?? undefined;
  return {
    items: mapAnalysisItems(analysis),
    source: "text",
    ...(analysis.mealName === null ? {} : { name: analysis.mealName }),
    ...(mealType === undefined ? {} : { mealType }),
    ...(uncertainty === undefined ? {} : { uncertainty }),
  };
}

export type AnalyzeMealTextFn = (args: {
  description: string;
  note?: string;
}) => Promise<MealTextAnalysis>;

export type TextAnalysisOutcome =
  | { readonly status: "analyzed"; readonly analysis: MealTextAnalysis }
  | { readonly status: "error"; readonly kind: AnalysisErrorKind };

// Pure controller around the action (todo-21 pattern): the component only
// maps the outcome onto UI state, so tests exercise every branch with a mocked
// analyze function.
export async function runMealTextAnalysis(
  analyze: AnalyzeMealTextFn,
  input: { readonly description: string; readonly mealType?: MealType },
): Promise<TextAnalysisOutcome> {
  const description = input.description.trim();
  const note = input.mealType === undefined ? undefined : `Meal type: ${input.mealType}`;
  try {
    const analysis = await analyze(note === undefined ? { description } : { description, note });
    console.log("[Frontend][TextLogFlow] Meal text analyzed", {
      itemCount: analysis.items.length,
      hasRange: analysis.uncertainty.hasRange,
      mealTypeGuess: analysis.mealTypeGuess ?? "none",
    });
    return { status: "analyzed", analysis };
  } catch (error: unknown) {
    const kind = classifyAnalysisError(error);
    console.error("[Frontend][TextLogFlow] Meal text analysis failed", {
      kind,
      error: error instanceof Error ? error.message : String(error),
    });
    return { status: "error", kind };
  }
}

function portionLabel(item: LoggingFlowReviewItem): string | null {
  const parts = [
    item.quantity === undefined ? null : String(item.quantity),
    item.unit ?? null,
  ].filter((part): part is string => part !== null && part.length > 0);
  return parts.length > 0 ? parts.join(" ") : null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface TextLogFlowProps {
  readonly demoMode: boolean;
  readonly onClose: () => void;
  // Prefill from startFlow("text", { note }) (e.g. todo 15 "Type instead").
  readonly initialNote?: string;
  // Test hooks (renderToStaticMarkup suites cannot click): seed UI states.
  readonly initialAnalysis?: MealTextAnalysis;
  readonly initialErrorKind?: AnalysisErrorKind;
  readonly initialCreditOpen?: boolean;
}

export function TextLogFlow({
  demoMode,
  onClose,
  initialNote,
  initialAnalysis,
  initialErrorKind,
  initialCreditOpen,
}: TextLogFlowProps) {
  const { startFlow } = useLoggingFlow();
  const analyzeAction = useAction(api.analyze.analyzeMealText);
  const [description, setDescription] = useState(initialNote ?? "");
  const [mealType, setMealType] = useState<MealType | undefined>(undefined);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<MealTextAnalysis | undefined>(initialAnalysis);
  const [errorKind, setErrorKind] = useState<AnalysisErrorKind | undefined>(initialErrorKind);
  const [creditOpen, setCreditOpen] = useState(initialCreditOpen === true);

  const handleAnalyze = async () => {
    if (!canAnalyzeText(description) || analyzing) return;
    setAnalyzing(true);
    setErrorKind(undefined);
    const outcome = await runMealTextAnalysis(analyzeAction, { description, mealType });
    setAnalyzing(false);
    if (outcome.status === "analyzed") {
      setAnalysis(outcome.analysis);
      return;
    }
    if (outcome.kind === "credits") {
      setCreditOpen(true);
      return;
    }
    setErrorKind(outcome.kind);
  };

  const handleReview = () => {
    if (analysis === undefined) return;
    const payload = buildTextReviewPayload(analysis, { mealType });
    console.log("[Frontend][TextLogFlow] Routing analysis to review", {
      itemCount: payload.items?.length ?? 0,
      hasUncertainty: payload.uncertainty !== undefined,
    });
    startFlow("review", payload);
  };

  const handleManual = () => {
    console.log("[Frontend][TextLogFlow] Enter-manually fallback to blank review", {
      mealType: mealType ?? "none",
    });
    startFlow("review", {
      items: [],
      source: "text",
      ...(mealType === undefined ? {} : { mealType }),
    });
  };

  const mealTypeChips = (
    <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Meal type (optional)">
      {TEXT_MEAL_TYPES.map((type) => {
        const active = mealType === type;
        return (
          <button
            key={type}
            type="button"
            data-testid={`text-log-meal-${type}`}
            aria-pressed={active}
            onClick={() => setMealType(active ? undefined : type)}
            className={cn(
              "inline-flex min-h-[44px] items-center rounded-full border px-4 text-sm font-medium transition-all duration-200 ease-out active:scale-[0.98] select-none",
              active
                ? "border-accent bg-accent-light text-accent"
                : "border-border bg-surface text-text-muted active:bg-muted",
            )}
          >
            {MEAL_TYPE_LABELS[type]}
          </button>
        );
      })}
    </div>
  );

  let body;
  if (demoMode) {
    // Demo mode has no backend/auth: no dead Analyze button, just the manual
    // path into the (fully local) review screen.
    body = (
      <>
        <div data-testid="text-log-demo-note" className="rounded-xl border border-border bg-muted p-4">
          <p className="text-sm font-medium text-text">AI analysis needs a signed-in account</p>
          <p className="mt-1 text-xs leading-relaxed text-text-muted">
            Demo mode cannot analyze a description, but you can open the review and enter the meal yourself.
          </p>
        </div>
        {mealTypeChips}
        <button
          type="button"
          data-testid="text-log-manual"
          onClick={handleManual}
          className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3 text-base font-medium text-white transition-all duration-200 ease-out active:bg-accent-dark active:scale-[0.98] select-none"
        >
          Enter manually
        </button>
      </>
    );
  } else if (analysis !== undefined) {
    const items = mapAnalysisItems(analysis);
    const { hasRange, lowKcal, highKcal } = analysis.uncertainty;
    body = (
      <>
        <div data-testid="text-log-preview" className="rounded-xl border border-border bg-muted p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-text-muted">
            We found {items.length} {items.length === 1 ? "item" : "items"}
          </p>
          {analysis.mealName !== null && (
            <p data-testid="text-log-preview-name" className="mt-1 text-base font-semibold text-text">
              {analysis.mealName}
            </p>
          )}
          <ul className="mt-3 flex flex-wrap gap-2">
            {items.map((item, index) => {
              const portion = portionLabel(item);
              return (
                <li
                  key={`${item.name}-${index}`}
                  data-testid="text-log-chip"
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-xs text-text"
                >
                  <span className="truncate font-medium">{item.name}</span>
                  {portion !== null && <span className="shrink-0 text-text-muted">{portion}</span>}
                  <span className="shrink-0 font-semibold text-text">
                    {item.calories === undefined ? "kcal unknown" : `${item.calories} kcal`}
                  </span>
                </li>
              );
            })}
          </ul>
          {hasRange && lowKcal !== null && highKcal !== null && (
            <p data-testid="text-log-preview-range" className="mt-3 text-xs text-text-muted">
              Estimated range: {lowKcal}-{highKcal} kcal
            </p>
          )}
        </div>
        <button
          type="button"
          data-testid="text-log-review-btn"
          onClick={handleReview}
          className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3 text-base font-medium text-white transition-all duration-200 ease-out active:bg-accent-dark active:scale-[0.98] select-none"
        >
          Review meal
        </button>
        <button
          type="button"
          data-testid="text-log-edit"
          onClick={() => setAnalysis(undefined)}
          className="mt-2 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 py-3 text-base font-medium text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
        >
          Edit description
        </button>
      </>
    );
  } else {
    body = (
      <>
        <textarea
          data-testid="text-log-input"
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
            setErrorKind(undefined);
          }}
          rows={4}
          placeholder={TEXT_PLACEHOLDER}
          disabled={analyzing}
          className={cn(
            "w-full resize-none rounded-xl border border-border bg-muted px-4 py-3 text-base",
            "text-text placeholder:text-text-muted caret-accent",
            "focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "min-h-[96px]",
          )}
        />
        {mealTypeChips}
        {(errorKind === "failed" || errorKind === "unknown") && (
          <div data-testid="text-log-error" role="alert" className="mt-3 rounded-xl border border-border bg-muted p-4">
            <p className="text-sm font-medium text-danger">We could not analyze that description</p>
            <p className="mt-1 text-xs leading-relaxed text-text-muted">
              Check your connection and try again, or enter the meal yourself.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                data-testid="text-log-retry"
                onClick={() => void handleAnalyze()}
                className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 py-2 text-sm font-semibold text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
              >
                Retry
              </button>
              <button
                type="button"
                data-testid="text-log-manual"
                onClick={handleManual}
                className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 py-2 text-sm font-semibold text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
              >
                Enter manually
              </button>
            </div>
          </div>
        )}
        <button
          type="button"
          data-testid="text-log-analyze"
          onClick={() => void handleAnalyze()}
          disabled={!canAnalyzeText(description) || analyzing}
          aria-busy={analyzing}
          className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3 text-base font-medium text-white transition-all duration-200 ease-out active:bg-accent-dark active:scale-[0.98] select-none disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {analyzing && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
          {analyzing ? "Analyzing..." : "Analyze"}
        </button>
      </>
    );
  }

  return (
    <>
      <div
        data-testid="text-log-scrim"
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex max-h-[90vh] flex-col justify-end p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
        <div className="pointer-events-auto mx-auto w-full max-w-xl overflow-y-auto rounded-2xl bg-surface ob-sheet-up" data-testid="text-log-flow">
          <div className="sticky top-0 z-10 flex items-center justify-between gap-2 rounded-t-2xl border-b border-border bg-surface px-5 py-2">
            <h3 className="text-lg font-semibold text-text">Describe your meal</h3>
            <button
              type="button"
              data-testid="text-log-close"
              aria-label="Close flow"
              className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted active:bg-muted active:scale-[0.98] select-none"
              onClick={onClose}
            >
              <X size={20} strokeWidth={2} />
            </button>
          </div>
          <div className="px-5 pb-6 pt-4">{body}</div>
        </div>
      </div>
      {creditOpen && <CreditDrawer onClose={() => setCreditOpen(false)} />}
    </>
  );
}
